'use client';

import { useMemo, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useTranslations } from 'next-intl';
import { HpApiWebApiError, HpOtpSource } from '@zg-rentals/ts-hotpads-api-client';
import type { HpWebListingFilterDto } from '@zg-rentals/ts-hotpads-api-client';
import type { AreaResponse } from '@/src/lib/api';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { useFeatureFlag } from '@/src/lib/feature-flags';
import { useOtpSignInGate } from '@/src/lib/hooks/auth';
import {
  useCreateInquiry,
  useCurrentArea,
  useCurrentUser,
  useInquiryPrefill,
  userListingKeys,
} from '@/src/lib/hooks/query';
import { inquiryKeys } from '@/src/lib/hooks/query/queryKeys';
import { useOptionalFilters } from '@/src/lib/filters';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import { filterStateToV3ListingFilterDto } from '@/src/lib/filters/filterStateToV3';
import { toSearchScope } from '@/src/lib/filters/searchScope';
import { getActiveSearchHdpBackPath } from '@/src/lib/navigation/searchHdpHistory';
import { useAllRentalsSlug } from '@/src/lib/seo/AllRentalsSlugProvider';
import { parseSrpPath } from '@/src/lib/seo/srpPath';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useSimilarListings } from '@/src/components/features/property/hooks/useSimilarListings';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';
import { analytics } from '@/src/components/features/property/lib';
import { getContactFormClickstreamContext } from '@/src/components/features/property/lib/analytics';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { hasSeenRenterProfilePrompt, markRenterProfilePromptShown } from './renterProfilePromptTracking';
import type {
  ContactClickstreamContext,
  ContactFormHostMode,
  ContactFormInputValues,
  ContactFormMode,
  ContactFormSavedSearchContext,
  ContactFormServerErrors,
  ContactSubmissionContext,
} from './types';

type UseHdpContactSubmissionOptions = {
  listing?: PropertyDetailListing | null;
  mode: ContactFormMode;
  formMode: ContactFormHostMode;
  clickstreamContext?: ContactClickstreamContext;
  /** Receives every completed submission, including one that finishes after sign-in. */
  onSubmitted: (context: ContactSubmissionContext) => void;
  /** Fires as the email-code step opens and closes, for a host that retitles its chrome. */
  onSignInOpenChange?: (open: boolean) => void;
};

/**
 * What the send after sign-in keeps from the moment the renter pressed the CTA: their values,
 * and the submission id the lead analytics already went out with.
 */
type PendingInquiry = {
  values: ContactFormInputValues;
  rentalSubmitId: string;
};

type ParsedSearchPath = {
  resourceId: string;
  searchSlug: string;
};

const DEFAULT_SERVER_ERRORS: ContactFormServerErrors = {
  emailError: '',
  phoneError: '',
  formError: '',
};

function formatPhoneForInquiry(raw: string) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function createRentalSubmitId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseSearchPath(searchPath: string | null, allRentalsSlug: string): ParsedSearchPath | null {
  const srpPath = searchPath ? parseSrpPath(searchPath, allRentalsSlug) : null;
  return srpPath?.resourceId ? { resourceId: srpPath.resourceId, searchSlug: srpPath.slug } : null;
}

function hasUsableBounds(area: AreaResponse | undefined): area is AreaResponse & {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} {
  return (
    typeof area?.minLat === 'number' &&
    typeof area.maxLat === 'number' &&
    typeof area.minLon === 'number' &&
    typeof area.maxLon === 'number'
  );
}

function getSavedSearchContext(
  area: AreaResponse | undefined,
  parsedSearchPath: ParsedSearchPath | null,
  filters: FilterState,
  allRentalsSlug: string,
): ContactFormSavedSearchContext | null {
  const legacyAreaId = area?.legacyAreaId ?? area?.id;
  const searchScope = toSearchScope(area);
  const locationText = area?.name ?? area?.city ?? area?.resourceId;
  const searchPath =
    parsedSearchPath ??
    (area?.resourceId
      ? {
          resourceId: area.resourceId,
          searchSlug: allRentalsSlug,
        }
      : null);

  if ((!legacyAreaId && !searchScope) || !locationText || !searchPath || !hasUsableBounds(area)) {
    return null;
  }

  return {
    areaId: legacyAreaId,
    resourceId: searchPath.resourceId,
    searchSlug: searchPath.searchSlug,
    locationText,
    areaBounds: {
      minLat: area.minLat,
      maxLat: area.maxLat,
      minLon: area.minLon,
      maxLon: area.maxLon,
    },
    filters,
    searchScope,
  };
}

/**
 * The search context the inquiry is sent from. v3 `create` only decides the saved-search
 * prompt when the request carries one, so this replaces the standalone
 * `displaySearchPromptPostInquiry` call the prompt used to come from.
 */
function buildInquirySearchFilter(
  savedSearchContext: ContactFormSavedSearchContext | null,
): HpWebListingFilterDto | undefined {
  if (!savedSearchContext) return undefined;

  return filterStateToV3ListingFilterDto(
    savedSearchContext.filters,
    savedSearchContext.areaBounds,
    savedSearchContext.areaId,
    savedSearchContext.searchScope,
  );
}

function getAvailableSimilarListings(
  similarListings: Array<SimilarListing> | undefined,
  listing: PropertyDetailListing,
) {
  const listingAlias = listing.aliasEncoded;
  const listingMaloneLotId = listing.maloneLotIdEncoded;

  return (similarListings ?? [])
    .filter((similarListing) => {
      const aliasMatches = Boolean(listingAlias) && similarListing.aliasEncoded === listingAlias;
      const lotMatches =
        Boolean(listingMaloneLotId) &&
        (similarListing.maloneLotIdEncoded === listingMaloneLotId ||
          similarListing.lotIdEncoded === listingMaloneLotId);

      return Boolean(similarListing.aliasEncoded) && !aliasMatches && !lotMatches;
    })
    .slice(0, 5);
}

function getInquiryText(values: ContactFormInputValues) {
  return [values.userMessage, values.lowIncomeMessage]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('\n\n');
}

export function useHdpContactSubmission({
  listing,
  mode,
  formMode,
  clickstreamContext,
  onSubmitted,
  onSignInOpenChange,
}: UseHdpContactSubmissionOptions) {
  const t = useTranslations('contactForm');
  const queryClient = useQueryClient();
  const filterContext = useOptionalFilters();
  const filters = filterContext?.filters ?? DEFAULT_FILTERS;
  const mockInquiryEnabled = useFeatureFlag('mock-inquiry');
  const { data: currentUser } = useCurrentUser();
  const { data: currentArea } = useCurrentArea();
  const { data: similarListings } = useSimilarListings({
    maloneLotIdEncoded: listing?.maloneLotIdEncoded,
    unit: listing?.unit,
  });
  // Signed out the endpoint can only 401, and the renter has no profile to prefill from.
  const { data: inquiryPrefill } = useInquiryPrefill({ enabled: Boolean(currentUser?.loggedIn) });
  const createInquiry = useCreateInquiry();
  const signInGate = useOtpSignInGate<PendingInquiry>({
    onSignedIn: async ({ values, rentalSubmitId }) => {
      const context = await sendInquiry(values, rentalSubmitId, true);
      if (context) onSubmitted(context);
    },
    onOpenChange: onSignInOpenChange,
    source: HpOtpSource.CONTACT,
  });
  const [serverErrors, setServerErrors] = useState<ContactFormServerErrors>(DEFAULT_SERVER_ERRORS);
  const resolvedClickstreamContext = useMemo<ContactClickstreamContext>(
    () => ({
      triggerLocation: clickstreamContext?.triggerLocation ?? 'home_details_halo',
      triggerObject:
        formMode === 'inline'
          ? 'home_details_component|inline_form'
          : (clickstreamContext?.triggerObject ?? 'home_details_component|summary'),
    }),
    [clickstreamContext?.triggerLocation, clickstreamContext?.triggerObject, formMode],
  );
  const submitClickstreamContext = getContactFormClickstreamContext(resolvedClickstreamContext, 'submit', formMode);

  const allRentalsSlug = useAllRentalsSlug();
  const savedSearchContext = useMemo(() => {
    const parsedSearchPath = parseSearchPath(getActiveSearchHdpBackPath(), allRentalsSlug);
    return getSavedSearchContext(currentArea, parsedSearchPath, filters, allRentalsSlug);
  }, [currentArea, filters, allRentalsSlug]);

  const searchFilter = useMemo(() => buildInquirySearchFilter(savedSearchContext), [savedSearchContext]);

  const isLoading =
    !mockInquiryEnabled && (currentUser === undefined || signInGate.isSending || createInquiry.isPending);

  const serverErrorsForInquiryFailure = (error: unknown): ContactFormServerErrors => {
    switch (v3ErrorReason(error)) {
      case HpApiWebApiError.INVALID_PHONE:
        return { ...DEFAULT_SERVER_ERRORS, phoneError: t('errorContactValidation') };
      case HpApiWebApiError.INVALID_EMAIL:
        return { ...DEFAULT_SERVER_ERRORS, emailError: t('errorContactValidation') };
      case HpApiWebApiError.ACCOUNT_DEACTIVATED:
        return { ...DEFAULT_SERVER_ERRORS, emailError: t('errorAccountDeactivated') };
      default:
        return { ...DEFAULT_SERVER_ERRORS, formError: t('errorSubmit') };
    }
  };

  const sendInquiry = async (
    values: ContactFormInputValues,
    rentalSubmitId: string,
    isLoggedIn: boolean,
  ): Promise<ContactSubmissionContext | null> => {
    if (!listing?.aliasEncoded) {
      setServerErrors((prev) => ({ ...prev, formError: t('errorSubmit') }));
      return null;
    }

    const text = getInquiryText(values);
    const formattedPhone = formatPhoneForInquiry(values.userPhoneNumber);

    try {
      let displaySavedSearchPrompt = mockInquiryEnabled;

      if (!mockInquiryEnabled) {
        // No email field: v3 takes the inquirer address from the authenticated session,
        // which is what makes the sign-in gate above load-bearing.
        const response = await createInquiry.mutateAsync({
          postData: {
            listingAlias: listing.aliasEncoded,
            name: values.userName,
            phone: formattedPhone,
            text,
            rentalSubmitId,
            requestToApply: mode === 'requestToApply',
            ...(values.numBeds ? { beds: values.numBeds } : {}),
            ...(values.numBaths ? { baths: values.numBaths } : {}),
            ...(values.moveDate ? { moveDate: values.moveDate } : {}),
            ...(searchFilter ? { searchFilter } : {}),
          },
        });

        displaySavedSearchPrompt = Boolean(response.data?.displaySavedSearchPrompt);
      }

      if (!isWaitlistedListing(listing)) {
        const email = currentUser?.loggedIn ? (currentUser.data?.email ?? values.userEmail) : values.userEmail;
        queryClient.setQueryData(inquiryKeys.contacted(listing.aliasEncoded, email), Date.now());
        if (!currentUser?.loggedIn) {
          // Signed-out submits only exist under mock-inquiry; the card-level contacted
          // hook reads the anonymous key for sessions without an account email.
          queryClient.setQueryData(inquiryKeys.contacted(listing.aliasEncoded, ''), Date.now());
        }
      }

      // No message: an unedited default names this listing, and the prefill endpoint already
      // decides whether the last message is person-level enough to carry forward.
      queryClient.setQueryData(inquiryKeys.prefill(), {
        email: values.userEmail,
        name: values.userName,
        phone: formattedPhone,
      });
      queryClient.invalidateQueries({ queryKey: userListingKeys.all });

      const shouldShowRenterProfile = isLoggedIn && !hasSeenRenterProfilePrompt();
      if (shouldShowRenterProfile) {
        markRenterProfilePromptShown();
      }

      return {
        listing,
        mode,
        formMode,
        clickstreamContext: resolvedClickstreamContext,
        inquiry: {
          name: values.userName,
          email: values.userEmail,
          phone: formattedPhone,
          text,
        },
        isLoggedIn,
        isWaitlist: isWaitlistedListing(listing),
        shouldShowRenterProfile,
        shouldShowSavedSearch: Boolean(displaySavedSearchPrompt && savedSearchContext),
        savedSearchContext,
        similarListings: getAvailableSimilarListings(similarListings, listing),
      };
    } catch (error) {
      setServerErrors(serverErrorsForInquiryFailure(error));
      return null;
    }
  };

  /**
   * Opens a submission: mints the id the analytics and the inquiry must share, and fires the
   * lead events. HP-8576 legacy parity puts those before any network write, and they fire
   * exactly once per submission — the send resumed after sign-in reuses this id rather than
   * emitting a second pair for the same lead. Returns null when the submission cannot start.
   */
  const openSubmission = (): string | null => {
    // A second press while the first is in flight is a duplicate lead, not a retry.
    if (signInGate.isSending || createInquiry.isPending) return null;

    setServerErrors(DEFAULT_SERVER_ERRORS);

    if (!mockInquiryEnabled && currentUser === undefined) return null;

    if (!listing?.aliasEncoded) {
      setServerErrors((prev) => ({ ...prev, formError: t('errorSubmit') }));
      return null;
    }

    const rentalSubmitId = createRentalSubmitId();

    analytics.emitTrackContactClickEvent({
      ...submitClickstreamContext,
      listing,
      mode,
      phase: 'submit',
      rentalSubmitId,
    });

    analytics.emitTrackEmailLeadEvent({
      listing,
      ...submitClickstreamContext,
      triggerSource:
        mode === 'requestToApply' ? 'button_to_complete_request_to_apply_form' : 'button_to_complete_contact_form',
    });

    return rentalSubmitId;
  };

  /**
   * Sends the inquiry, signing a signed-out renter in first. The step is an auth gate and
   * nothing more: once the code verifies the renter is back on their own filled-in form and the
   * send runs from there, reusing this submission's id so the lead analytics fire only once. A
   * rejection (an invalid phone, say) then shows where it was raised, and a resubmit needs no
   * second code because the session is live.
   */
  const submitInquiry = async (values: ContactFormInputValues) => {
    const rentalSubmitId = openSubmission();
    if (!rentalSubmitId) return;

    // mock-inquiry never posts, so it needs no session.
    if (mockInquiryEnabled) {
      const context = await sendInquiry(values, rentalSubmitId, Boolean(currentUser?.loggedIn));
      if (context) onSubmitted(context);
      return;
    }

    const signIn = await signInGate.requireSignIn(values.userEmail, { values, rentalSubmitId });
    if (signIn.status === 'sendFailed') {
      setServerErrors({
        ...DEFAULT_SERVER_ERRORS,
        [signIn.field === 'email' ? 'emailError' : 'formError']: signIn.message,
      });
    }
  };

  return {
    inquiryPrefill,
    isLoading,
    serverErrors,
    signInGate,
    submitInquiry,
  };
}
